import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLinkWithHref, RouterOutlet } from '@angular/router';
import { version } from '../../package.json';
import { injectSignOut } from './auth/commands/sign-out';
import { injectUser } from './auth/data/user';
import { isKaizenEmail } from './auth/utils/is-kaizen-email';
import AppUpdateBanner from './errors/app-update-banner/app-update-banner';
import Footer from './layout/footer/footer';
import SideNavOuterToolbar from './layout/side-nav-outer-toolbar/side-nav-outer-toolbar';
import { Screen } from './screen/screen';
import { Theme } from './theme/theme';
import { injectIsHandset } from './ui/is-handset';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Footer,
    SideNavOuterToolbar,
    NgOptimizedImage,
    RouterLinkWithHref,
    AppUpdateBanner,
  ],
  template: `
    <app-update-banner />
    <app-side-nav-outer-toolbar appTitle="FirmFirst">
      <router-outlet />
      <app-footer>
        <div style="display: flex; align-items: center; gap: 1em; flex-wrap: wrap">
          @if (isDarkMode()) {
            <img
              ngSrc="logos/firmfirst-darkmode.svg"
              alt="FirmFirst"
              height="100"
              width="380"
              priority
            />
          } @else {
            <img
              ngSrc="logos/firmfirst-primary.svg"
              alt="FirmFirst"
              height="80"
              width="340"
              priority
            />
          }

          <div>
            Copyright © 2025 FirmFirst Inc.
            <br />
            All trademarks or registered trademarks are property of their respective owners.
          </div>
          @if (hasKaizenEmail()) {
            <div class="dx-badge" style="margin-left: auto">
              <a style="text-decoration: none; color: inherit" [routerLink]="['/changelog']">
                {{ appVersion }}
              </a>
            </div>
          } @else {
            <div class="dx-badge" style="margin-left: auto">{{ appVersion }}</div>
          }
        </div>
      </app-footer>
    </app-side-nav-outer-toolbar>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
    }

    app-side-nav-outer-toolbar {
      flex: 1;
      min-height: 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClass()',
  },
})
export class App {
  private readonly signOut = injectSignOut();
  private readonly screen = inject(Screen);
  private readonly theme = inject(Theme);

  protected readonly isHandset = injectIsHandset();

  private readonly user = injectUser();

  protected readonly hasKaizenEmail = computed(() => {
    const email = this.user()?.email;
    return email ? isKaizenEmail(email) : false;
  });

  protected readonly isDarkMode = computed(() => this.theme.effectiveTheme() === 'dark');

  protected hostClass = computed(() => {
    const sizes = this.screen.sizes();
    const sizeClassName = Object.entries(sizes)
      .filter(([, isActive]) => isActive)
      .map(([className]) => className)
      .join(' ');
    return `${sizeClassName} app`;
  });

  protected readonly appVersion = version;

  protected async onSignOutClicked() {
    await this.signOut();
  }
}
